/**
 * Router-compat shim — bridges @/lib/router-compat v6 call sites to
 * @tanstack/react-router without hand-rewriting every component.
 * This is the same load-bearing pattern used in Klar's dev-copy migration.
 */
import {
  useNavigate as tsNavigate,
  useLocation as tsLocation,
  useParams as tsParams,
  useSearch as tsSearch,
  useRouter,
  Link as TSLink,
  Navigate as TSNavigate,
  Outlet as TSOutlet,
  createRootRoute,
  createRoute,
  createRouter,
  createMemoryHistory,
  RouterProvider,
} from "@tanstack/react-router";
import { useMemo, useCallback, useEffect, useRef, useState, forwardRef, type ComponentProps, type ReactNode } from "react";

// ---------- shared URL parsing ----------

function parseTo(to: string): { pathname: string; search?: Record<string, string>; hash?: string } {
  const [beforeHash, hashStr] = (to ?? "").split("#");
  const [pathname, searchStr] = beforeHash.split("?");
  return {
    // react-router keeps the current path for search-only ("?a=1") and
    // hash-only ("#section") targets; TanStack's "." means current route.
    pathname: pathname || ".",
    search: searchStr ? Object.fromEntries(new URLSearchParams(searchStr)) : undefined,
    hash: hashStr || undefined,
  };
}

// ---------- useNavigate ----------

type NavigateOptions = { replace?: boolean; state?: unknown };

type NavigateFn = {
  (to: string | number, options?: NavigateOptions): void;
  (delta: number): void;
};

export function useNavigate(): NavigateFn {
  const tsNav = tsNavigate();
  const router = useRouter();
  return useCallback((to: string | number, options?: NavigateOptions) => {
    if (typeof to === "number") {
      router.history.go(to);
      return;
    }
    const { pathname, search, hash } = parseTo(to);
    tsNav({
      to: pathname,
      search: search as never,
      hash,
      state: options?.state as never,
      replace: options?.replace,
    });
  }, [tsNav, router]) as NavigateFn;
}

// ---------- useLocation ----------

export function useLocation() {
  const loc = tsLocation();
  return useMemo(
    () => ({
      pathname: loc.pathname,
      search: loc.searchStr ? `?${loc.searchStr}` : "",
      hash: loc.hash ?? "",
      state: (loc.state ?? null) as unknown,
      key: loc.pathname + (loc.searchStr ?? ""),
    }),
    [loc.pathname, loc.searchStr, loc.hash, loc.state],
  );
}

// ---------- useNavigationType ----------

/**
 * Equivalente ao useNavigationType do react-router: informa se a navegação
 * atual foi PUSH/REPLACE (clique) ou POP (voltar/avançar do navegador).
 * A subscription do history do TanStack entrega exatamente essa ação.
 */
export function useNavigationType(): "POP" | "PUSH" | "REPLACE" {
  const router = useRouter();
  const [type, setType] = useState<"POP" | "PUSH" | "REPLACE">("POP");

  useEffect(() => {
    return router.history.subscribe(({ action }: { action?: unknown }) => {
      // O history do TanStack usa BACK/FORWARD/GO onde o react-router usava POP.
      const raw = (action as { type?: string } | undefined)?.type;
      if (raw === "PUSH" || raw === "REPLACE") {
        setType(raw);
      } else if (raw === "BACK" || raw === "FORWARD" || raw === "GO") {
        setType("POP");
      }
    });
  }, [router]);

  return type;
}

// ---------- useParams ----------

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  return tsParams({ strict: false } as never) as T;
}


// ---------- useSearchParams (@/lib/router-compat compat) ----------

export function useSearchParams(): [URLSearchParams, (init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams), opts?: { replace?: boolean }) => void] {
  const loc = tsLocation();
  const nav = tsNavigate();
  const router = useRouter();
  const params = useMemo(() => new URLSearchParams(loc.searchStr ?? ""), [loc.searchStr]);
  const setParams = useCallback(
    (
      init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => {
      // Functional updaters read the router's live location, not the render
      // snapshot — react-router passes call-time params, and chained updates
      // within one tick must see each other's writes.
      const live = router.state.location;
      const current = new URLSearchParams(live.searchStr ?? "");
      const next =
        typeof init === "function"
          ? init(current)
          : init instanceof URLSearchParams
            ? init
            : new URLSearchParams(init);
      const searchObj: Record<string, string> = {};
      next.forEach((v, k) => { searchObj[k] = v; });
      nav({ to: live.pathname, search: searchObj as never, replace: opts?.replace });
    },
    [nav, router],
  );
  return [params, setParams];
}

// ---------- Link ----------

export type LinkProps = Omit<ComponentProps<typeof TSLink>, "to" | "state" | "replace"> & {
  to: string;
  replace?: boolean;
  state?: unknown;
  children?: ReactNode;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, replace, state, children, ...rest },
  ref,
) {
  const { pathname, search, hash } = parseTo(to);
  return (
    <TSLink
      ref={ref as never}
      to={pathname as never}
      search={search as never}
      hash={hash}
      replace={replace}
      state={state as never}
      {...((rest ?? {}) as Record<string, unknown>)}
    >
      {children}
    </TSLink>
  );
});


// ---------- Navigate ----------

/**
 * Redireciona uma única vez. O `state` costuma ser um objeto novo em cada
 * renderização (ex.: `{ from: location }`); navegar direto no render faria
 * o React entrar em laço infinito de atualizações.
 */
export function Navigate({ to, replace, state }: { to: string; replace?: boolean; state?: unknown }) {
  const nav = tsNavigate();
  const lastTo = useRef<string | null>(null);
  const target = parseTo(to);
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    if (lastTo.current === to) return;
    lastTo.current = to;
    nav({
      to: target.pathname as never,
      search: target.search as never,
      hash: target.hash,
      state: stateRef.current as never,
      replace: replace ?? true,
    });
    // Reage só à mudança de destino, nunca ao objeto `state`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);
  return null;
}

// ---------- Outlet ----------

export const Outlet = TSOutlet;

// ---------- NavLink (minimal) ----------

export const NavLink = Link;

// ---------- MemoryRouter (só para testes unitários) ----------

/**
 * Envolve a árvore em um router de memória, para componentes que usam Link
 * poderem ser renderizados isolados nos testes.
 */
export function MemoryRouter({ children, initialPath = "/" }: { children?: ReactNode; initialPath?: string }) {
  const router = useMemo(() => {
    const rootRoute = createRootRoute({ component: () => <>{children}</> });
    const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/" });
    const splatRoute = createRoute({ getParentRoute: () => rootRoute, path: "$" });
    return createRouter({
      routeTree: rootRoute.addChildren([indexRoute, splatRoute]),
      history: createMemoryHistory({ initialEntries: [initialPath] }),
    });
  }, [children, initialPath]);

  return <RouterProvider router={router as never} />;
}
